import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import { useMemo } from "react";
import { Controller, Path, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbBuildingAirport } from "react-icons/tb";
import { ZodError } from "zod";

import { Airpots, airpots } from "@/constants/airpots";
import { getValueByPath } from "@/helpers/object";
import { AirportOption } from "@/pages/Plannn/components/table/PlanAirportColumn";

import { CommonLabel } from "./CommonInput";

function AirportField<T extends Record<string, unknown>>({
    airport,
    loading,
    className,
    label,
    formPath,
}: {
    airport: Airpots | null;
    loading: boolean;
    className: string;
    label: string;
    formPath: Path<T>;
}) {
    const { t } = useTranslation();
    const { control, formState } = useFormContext();
    const error = useMemo(() => getValueByPath<ZodError>(formState.errors, formPath), [formState]);

    return (
        <CommonLabel label={t(label)} className={className}>
            <Controller
                name={formPath}
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        classes={{
                            inputRoot: " !h-[47px]",
                        }}
                        sx={() => ({
                            "label + &": {
                                marginTop: 20,
                            },
                            "& .MuiInputBase-input": {
                                paddingBottom: 12,
                                paddingTop: 12,
                            },
                        })}
                        value={airport || null}
                        options={[Airpots.IST, Airpots.SAW]}
                        disabled={loading}
                        onChange={(_, value) => field.onChange(value)}
                        getOptionLabel={(val) => {
                            const item = airpots.find((airport) => airport.value == val);
                            return t(item?.value || "");
                        }}
                        renderOption={(props, val) => {
                            const { key, ...optionProps } = props;

                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    {...optionProps}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: 0,
                                        gap: 0,
                                    }}
                                >
                                    <AirportOption value={val} />
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t("placeholders.select", { field: t("airport") })}
                                error={!!error}
                                helperText={(error && error?.message) || ""}
                                variant="filled"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        transform: "translateY(-8px)",
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="end" className="  !-translate-x-1 !-translate-y-2">
                                            <TbBuildingAirport />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
        </CommonLabel>
    );
}

export default AirportField;
